import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../api';
import {
  CadastroRapidoCliente, useBusca, type Cliente, type Funcionario,
} from '../cadastros';
import { data, dataIso, numero, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { Area, Aviso, Campo, Dialogo, ErroDaApi } from '../ui';

/**
 * As peças de formulário da ordem de serviço.
 *
 * Separadas da ficha (`OsFicha.tsx`) e da lista (`Os.tsx`) pelo mesmo
 * motivo que os formulários de equipamento: a ficha é a tela que se lê, e
 * cada diálogo é uma tela que se preenche. Misturar as duas coisas num
 * arquivo só faz com que a leitura da ficha passe por trezentas linhas de
 * `useState` antes de chegar no que ela mostra.
 */

// ── O que a visão devolve ────────────────────────────────────────────

/**
 * A OS como `vw_ordem_servico` a entrega.
 *
 * Os números vêm como texto porque `count()` em PostgreSQL é `bigint`, e
 * o driver entrega `bigint` como string para não perder precisão. Quem
 * compara sem `Number()` compara texto — e `'10' < '9'` é verdadeiro.
 */
export interface Os {
  id: string;
  numero: string;
  obra_id: string;
  obra_codigo: string;
  obra_nome: string;
  municipio: string | null;
  uf: string | null;
  cliente_id: string;
  cliente: string;
  proposta_numero: string | null;
  prioridade: string;
  prazo_contratual: string | null;
  data_prevista_inicio: string | null;
  data_prevista_fim: string | null;
  data_realizada_inicio: string | null;
  data_realizada_fim: string | null;
  status_tecnico: string;
  status_relatorio: string;
  status_medicao: string;
  gestor_id: string | null;
  responsavel_tecnico_id: string | null;
  responsavel_comercial_id: string | null;
  gestor_nome: string | null;
  responsavel_tecnico_nome: string | null;
  responsavel_comercial_nome: string | null;
  /** Quem abriu: o INSERT da trilha (225). Só vem em `GET /os/:id`. */
  aberta_por_nome?: string | null;
  /**
   * 304 (RN-249): ensaios atrasados do LABORATÓRIO — o contador da aba
   * Cronograma. A conta é a da aba, no servidor. Só vem em `GET /os/:id`.
   */
  cronograma_atrasos?: number | string | null;
  observacoes: string | null;
  criado_em: string;
  atrasada: boolean;
  dias_para_prazo: number | string | null;
  prazo_guarda_dias: number | string | null;
  /** OS de treino: não entra na medição (0079). */
  demonstracao?: boolean;
  amostras_previstas_total: number | string;
  amostras_previstas_atendidas: number | string;
  // ── 0041 ──
  complemento: string | null;
  revisao: number | string;
  lotes_irmaos: number | string;
  revisao_em: string | null;
  // ── 0059: a situação se deriva; parar é decisão ──
  suspensa: boolean;
  suspensa_em: string | null;
  suspensa_por: string | null;
  suspensa_motivo: string | null;
  suspensa_por_nome: string | null;
  /** 0288 (RN-279): decisão de tirar a OS das filas — não é situação. */
  arquivada?: boolean;
  arquivada_em?: string | null;
  arquivada_motivo?: string | null;
  arquivada_por_nome?: string | null;
  cancelada: boolean;
  cancelada_em: string | null;
  cancelada_por: string | null;
  cancelada_motivo: string | null;
  cancelada_por_nome: string | null;
  ensaios_total: number | string;
  ensaios_aprovados: number | string;
  ensaios_em_execucao: number | string;
  ensaios_devolvidos: number | string;
  /* O avanço físico dos ensaios, 0 a 100 (0064). Nulo quando não há
     ensaio: a OS não está em 0 %, ela ainda não começou a ser medida. */
  avanco_pct: number | null;
  amostras_recebidas: number | string;
  /* Acrescentados na 0066, para a lista responder sem abrir a OS. */
  ultima_chegada?: string | null;
  parada_desde?: string | null;
  dias_parada?: number | string | null;
  escopo?: ItemEscopo[];
  amostras_previstas?: AmostraPrevistaResumo[];
  /* O escopo somado por MÉTODO, e o resumo dele (0149). */
  escopo_por_metodo?: EscopoPorMetodo[];
  escopo_resumo?: EscopoResumo | null;
  /* 0220 (RN-218, D2): nada programado ainda — o escopo muda livre. */
  escopo_livre?: boolean;
  /* 0220 (RN-f): o que a previsão marcou e não virou ensaio na chegada. */
  previsto_nao_entrou?: PrevistoQueNaoEntrou[];
}

export interface PrevistoQueNaoEntrou {
  amostra_id: string;
  amostra_codigo: string;
  prevista_id: string;
  identificacao_prevista: string | null;
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome: string;
}

/**
 * O escopo somado por MÉTODO (0149).
 *
 * «A quantidade é por método de ensaio. A medição quase sempre é por
 *  quantidade de método de ensaio» — Testesolo, 02/09/2026.
 */
export interface EscopoPorMetodo {
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome: string;
  metodo_grupo: string | null;
  quantidade_contratada: string;
  itens: string[];
  programados: number;
  cancelados: number;
  saldo: string;
  /* 0220 (D6): marcados em amostra por chegar — já descontam do saldo */
  reservados?: number;
}

interface EscopoResumo {
  metodos_no_escopo: number;
  ensaios_contratados: number;
  ensaios_programados: number;
  saldo: number;
  metodos_estourados: number;
  itens_sem_metodo: number;
  ensaios_reservados?: number;
}


export interface ItemEscopo {
  id: string;
  /* 0220: o item do catálogo — é o que «Incluir na OS» manda */
  item_medicao_id?: string;
  item: string;
  codigo: string;
  unidade: string;
  quantidade_prevista: string | number;
  observacoes: string | null;
  /** 283: a revisão em que o item passou a valer (0 = emissão). */
  revisao_inicio?: number;
}

interface AmostraPrevistaResumo {
  id: string;
  identificacao_prevista: string | null;
  origem_prevista: string | null;
  metodos: string[];
}

export interface AmostraPrevista {
  id: string;
  ordem: number;
  identificacao_prevista: string | null;
  origem_prevista: string | null;
  observacoes: string | null;
  atendida: boolean;
  amostra_id: string | null;
  amostra_codigo: string | null;
  ensaios: string | null;
  ensaios_previstos: number | string;
  /* 0162: o nome que o cliente deu à amostra ao chegar, e os ensaios
     prontos para desenhar — bateria inteira vira um cartão só. */
  identificacao_cliente?: string | null;
  ensaios_codigos?: EnsaioDaPrevisao[];
  /* 0190 (201): a coordenada prevista, de projeto ou estimada, nos dois
     jeitos — a tabela única da 204 a mostra nas linhas «Esperando». */
  latitude?: number | null;
  longitude?: number | null;
  origem_coordenada?: 'projeto' | 'estimada' | null;
  utm_fuso?: number | null;
  utm_e?: number | string | null;
  utm_n?: number | string | null;
  /* 0188: tipo e profundidade previstos. */
  tipo_amostra?: string | null;
  profundidade_inicial?: string | number | null;
  profundidade_final?: string | number | null;
  /* 0226 (240, D5): a previsão de chegada — a vigente e a primeira, de
     quem é a data, e há quantos dias passou da primeira. */
  previsao_chegada?: string | null;
  previsao_chegada_inicial?: string | null;
  previsao_origem?: 'cliente' | 'testesolo' | null;
  chegada_atrasada_dias?: number | null;
}

export interface EnsaioDaPrevisao {
  codigo: string;
  escopo: string | null;
  bateria: boolean;
  codigos?: string[];
}

interface Obra {
  id: string; codigo: string; nome: string;
  cliente_id: string; municipio: string | null; uf: string | null;
}

interface Metodo {
  id: string; codigo: string; nome: string;
  categoria: string; norma: string | null;
}

// ── Os três status ───────────────────────────────────────────────────

/**
 * Os valores que cada enum do banco aceita, na ordem do briefing (item 5).
 *
 * A ordem importa: ela é o percurso normal do serviço, e uma lista fora de
 * ordem faria o técnico procurar "concluída" no meio da lista.
 *
 * **A tela oferece todos, e não um subconjunto.** Não é descuido: a tabela
 * `os_transicao_permitida` existe desde a `0005` e está VAZIA de propósito
 * — `PD-41`, a Testesolo ainda não desenhou quais transições valem. Vazia,
 * o gatilho aceita qualquer uma. Esconder valores aqui seria a tela
 * inventando o fluxo que a empresa ainda não decidiu, que é exatamente o
 * que a restrição 25 do briefing proíbe. No dia em que a tabela receber
 * linhas, quem passa a recusar é o banco — e a recusa chega ao técnico com
 * a mensagem do gatilho, não com um botão que sumiu sem explicação.
 */
export const STATUS = {
  tecnico: ['rascunho', 'aguardando_aprovacao', 'aguardando_programacao',
    'programada', 'aguardando_amostras', 'amostras_recebidas',
    'ensaios_em_andamento', 'aguardando_dados', 'em_processamento',
    'com_pendencia', 'concluida', 'suspensa', 'cancelada'],
  relatorio: ['nao_iniciado', 'em_elaboracao', 'em_verificacao',
    'com_pendencia', 'aprovado', 'emitido', 'disponibilizado'],
  medicao: ['nao_iniciada', 'em_medicao', 'aguardando_conferencia',
    'aguardando_aprovacao', 'aprovada', 'emitida'],
} as const;

export type CampoStatus = keyof typeof STATUS;

export const TITULO_STATUS: Record<CampoStatus, string> = {
  tecnico: 'Situação técnica',
  relatorio: 'Situação do relatório',
  medicao: 'Situação da medição',
};

/** O que cada status responde — para quem nunca viu esta tela. */
export const DIZ_STATUS: Record<CampoStatus, string> = {
  tecnico: 'Onde o serviço está: da abertura da OS ao fim dos ensaios.',
  relatorio: 'O documento técnico: elaboração, verificação, aprovação e '
    + 'entrega ao cliente.',
  medicao: 'O boletim de quantitativos executados. Anda sozinho: medir '
    + 'não espera o relatório ficar pronto.',
};

// ── Quem pode ser o responsável técnico ──────────────────────────────

/**
 * Quem pode ser o RT da OS (`RN-227`, 274).
 *
 * Até a 274 o seletor oferecia todo funcionário ativo; agora oferece só
 * quem tem a assinatura «Ser responsável técnico da OS» — a lista vem do
 * servidor (`GET /os/responsaveis-tecnicos`), com a mesma regra que o
 * banco confere ao gravar. A tela não decide quem é RT.
 *
 * Tolera `403` como antes: quem não pode ver a lista continua editando o
 * resto da OS, e a dica diz por que o campo está bloqueado.
 */
export function useResponsaveisTecnicos(ativo = true) {
  const [lista, setLista] = useState<Funcionario[] | null>(null);
  const [semPermissao, setSemPermissao] = useState(false);

  useEffect(() => {
    if (!ativo) { setLista([]); return undefined; }
    const abortar = new AbortController();
    api<Funcionario[]>('/os/responsaveis-tecnicos', { sinal: abortar.signal })
      .then(setLista)
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return;
        if (e instanceof ErroApi && e.status === 403) setSemPermissao(true);
        setLista([]);
      });
    return () => abortar.abort();
  }, [ativo]);

  return { lista, semPermissao };
}

/**
 * O seletor do RT.
 *
 * `atual` é o RT que a OS já tem. Numa OS de antes da 274 ele pode não ter
 * a assinatura de RT — e continua aparecendo, marcado, até alguém trocar:
 * as OS que já existem ficam como estão (decisão do Humberto, 04/10/2026).
 */
export function SeletorResponsavelTecnico({
  valor, aoEscolher, dica, responsaveis, semPermissao, atual, erro,
}: {
  valor: string;
  aoEscolher(id: string): void;
  dica?: string;
  responsaveis: Funcionario[] | null;
  semPermissao: boolean;
  atual?: { id: string; nome: string | null } | null;
  erro?: string;
}) {
  const lista = responsaveis ?? [];
  const atualFora = !!atual?.id && !!responsaveis
    && !lista.some((f) => f.id === atual.id);
  const ninguem = !!responsaveis && lista.length === 0 && !semPermissao;
  return (
    <label className="campo">
      <span className="rotulo">Responsável técnico</span>
      <select
        value={valor}
        disabled={semPermissao || !responsaveis}
        onChange={(e) => aoEscolher(e.currentTarget.value)}
        aria-invalid={erro ? true : undefined}
        data-seletor-rt
      >
        <option value="">— não definido —</option>
        {atualFora && (
          <option value={atual!.id}>
            {`${atual!.nome ?? 'RT atual'} — sem a assinatura de RT`}
          </option>
        )}
        {lista.map((f) => (
          <option key={f.id} value={f.id}>
            {f.cargo ? `${f.nome} — ${f.cargo}` : f.nome}
          </option>
        ))}
      </select>
      {erro && <span className="erro">{erro}</span>}
      <span className="dica">
        {semPermissao
          ? 'Seu perfil não consulta quem pode ser responsável técnico, então '
            + 'este campo fica bloqueado. O resto da OS continua editável.'
          : ninguem
            ? 'Ninguém tem a assinatura «Ser responsável técnico da OS». Ela se '
              + 'dá em Usuários e acessos.'
            : atualFora && valor === atual!.id
              ? 'O RT desta OS foi definido antes da regra de RT e fica como '
                + 'está. Trocar, só por alguém com a assinatura de RT.'
              : dica ?? 'Opcional. Pode ser definido depois.'}
      </span>
    </label>
  );
}

// ── Obra: escolher ou cadastrar ──────────────────────────────────────

/**
 * A obra da OS — e o caminho para criá-la quando ela não existe.
 *
 * **Escolher a obra é escolher o número da OS.** O `codigo` da obra é o
 * número do projeto (`AAMMNNN`, sete dígitos) e é ele que aparece impresso
 * no relatório. O sistema não numera nada, e por isso não há campo de
 * número aqui: o que existe é o campo da obra.
 *
 * O cadastro fica embutido, e não num diálogo por cima do diálogo, por uma
 * razão prática: cadastrar obra exige escolher cliente, e cadastrar cliente
 * já é um diálogo. Três camadas empilhadas no celular são três telas
 * cobrindo uma à outra, e quem fecha a de cima por engano perde as três.
 */
export function EscolhaDeObra({
  obra, aoEscolher, numeroPrevisto,
}: {
  obra: Obra | null;
  aoEscolher(o: Obra | null): void;
  /** O número que a OS vai levar — com a letra do lote, quando houver. */
  numeroPrevisto?: string;
}) {
  const rota = useCallback(
    (alvo: string) => `/obras?busca=${encodeURIComponent(alvo)}&status=ativo`, []);
  const { texto, setTexto, achados, semPermissao } = useBusca<Obra>(rota);
  const [cadastrando, setCadastrando] = useState(false);
  const { pode } = useSessao();
  const vazio = !!achados && achados.length === 0
    && texto.trim().length >= 2 && !semPermissao;

  if (obra) {
    return (
      <div className="campo">
        <span className="rotulo">Obra</span>
        <div className="linha">
          <div className="item" style={{ cursor: 'default' }}>
            <span className="cod mono">{obra.codigo}</span>
            <span className="sub">{obra.nome}</span>
            {(obra.municipio || obra.uf) && (
              <span className="meta">
                {[obra.municipio, obra.uf].filter(Boolean).join(' — ')}
              </span>
            )}
          </div>
          <button
            type="button" className="botao secundario estreito"
            style={{ flex: '0 0 auto' }}
            onClick={() => { aoEscolher(null); setTexto(''); }}
          >
            Trocar
          </button>
        </div>
        <span className="dica">
          O número desta OS será{' '}
          <b className="mono">{numeroPrevisto ?? obra.codigo}</b>
          {numeroPrevisto && numeroPrevisto !== obra.codigo
            ? ' — o número do projeto da obra, com a letra do lote.'
            : ' — o mesmo número do projeto da obra.'}
        </span>
      </div>
    );
  }

  if (cadastrando) {
    return (
      <CadastroDeObra
        nomeDigitado={texto.trim()}
        aoCancelar={() => setCadastrando(false)}
        aoCriar={(o) => { setCadastrando(false); aoEscolher(o); }}
      />
    );
  }

  return (
    <div className="campo">
      <span className="rotulo">Obra</span>
      <input
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Número da obra, nome ou município"
        autoComplete="off"
        aria-label="Buscar obra"
      />
      <span className="dica">
        Obrigatória. É a obra que dá o número à OS. Se a obra já tiver OS, a nova ganha uma letra de lote — e pede motivo.
      </span>
      {semPermissao && <span className="dica">Seu perfil não consulta obras.</span>}

      {achados && achados.length > 0 && (
        <div className="lista" style={{ marginTop: '.5rem' }}>
          {achados.map((o) => (
            <button key={o.id} type="button" className="item"
              onClick={() => aoEscolher(o)}>
              <span className="cod mono">{o.codigo}</span>
              <span className="sub">{o.nome}</span>
              {(o.municipio || o.uf) && (
                <span className="meta">
                  {[o.municipio, o.uf].filter(Boolean).join(' — ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {vazio && (
        <div className="saida-do-beco">
          <span className="dica">
            Nenhuma obra ativa com “{texto.trim()}”.
          </span>
          {pode('cadastro.editar') ? (
            <button type="button" className="botao secundario"
              onClick={() => setCadastrando(true)}>
              Cadastrar obra
            </button>
          ) : (
            <span className="dica">
              Seu perfil não cadastra obras. Peça a quem administra os
              cadastros — sem obra não há número, e sem número não há OS.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O cadastro da obra, embutido na abertura da OS.
 *
 * O código é digitado e validado nos dois lugares — aqui, para o erro cair
 * no campo certo antes de ir à rede, e no banco
 * (`obra_codigo_numero_do_projeto`), porque validação que só existe na tela
 * é validação que o próximo caminho não tem.
 */
function CadastroDeObra({
  nomeDigitado, aoCancelar, aoCriar,
}: { nomeDigitado: string; aoCancelar(): void; aoCriar(o: Obra): void }) {
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState(nomeDigitado);
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  const codigoOk = /^[0-9]{2}(0[1-9]|1[0-2])[1-9][0-9]{2}$/.test(codigo.trim());
  const erroApi = erro instanceof ErroApi ? erro : null;

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      const o = await api<Obra>('/obras', {
        metodo: 'POST',
        corpo: {
          codigo: codigo.trim(),
          clienteId: cliente!.id,
          nome: nome.trim(),
          municipio: municipio.trim() || undefined,
          uf: uf.trim() ? uf.trim().toUpperCase() : undefined,
        },
      });
      aoCriar(o);
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  return (
    <div className="bloco">
      <h3>Cadastrar obra</h3>
      <ErroDaApi erro={erro} />

      <EscolhaDeCliente cliente={cliente} aoEscolher={setCliente} />

      {/*
        * «Na criação da OS tem o número do projeto e número da obra.
        *  Vamos manter apenas número da obra, pois se trata da mesma
        *  informação.» — Testesolo, 02/09/2026.
        *
        * Sempre foi UM campo só no banco (`obra.codigo`); o que havia
        * eram dois NOMES para ele — «número do projeto» aqui e «número da
        * obra» no resto do sistema. O efeito apareceu num print: alguém
        * digitou o número no campo «Nome da obra», porque «número do
        * projeto» não pareceu ser o número da obra.
        */}
      <Campo
        rotulo="Número da obra"
        inputMode="numeric"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 7))}
        erro={erroApi?.campo('codigo')
          ?? (codigo.length === 7 && !codigoOk
            ? 'Mês tem que ser de 01 a 12 e o sequencial de 100 a 999.'
            : undefined)}
        dica="Sete dígitos, AAMMNNN — ano, mês de abertura e sequencial. Ex.: 2608194. É este número que a OS vai levar."
      />
      <Campo
        rotulo="Nome da obra"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        erro={erroApi?.campo('nome')}
        dica="Como aparece no contrato e no cabeçalho do relatório."
      />
      <div className="linha">
        <Campo
          rotulo="Município"
          value={municipio}
          onChange={(e) => setMunicipio(e.target.value)}
          erro={erroApi?.campo('municipio')}
        />
        <Campo
          rotulo="UF"
          value={uf}
          maxLength={2}
          onChange={(e) => setUf(e.target.value.replace(/[^a-zA-Z]/g, ''))}
          erro={erroApi?.campo('uf')}
        />
      </div>

      <div className="linha" style={{ marginTop: '.4rem' }}>
        <button
          type="button" className="botao"
          disabled={!cliente || !codigoOk || nome.trim().length < 3 || salvando}
          onClick={() => { void salvar(); }}
        >
          {salvando ? 'Cadastrando…' : 'Cadastrar e usar'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoCancelar} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/** O cliente da obra. Mesmo desenho do passo 1 do recebimento. */
function EscolhaDeCliente({
  cliente, aoEscolher,
}: { cliente: Cliente | null; aoEscolher(c: Cliente | null): void }) {
  const rota = useCallback(
    (alvo: string) => `/clientes?busca=${encodeURIComponent(alvo)}`, []);
  const { texto, setTexto, achados, semPermissao } = useBusca<Cliente>(rota);
  const [cadastrando, setCadastrando] = useState(false);
  const { pode } = useSessao();
  const vazio = !!achados && achados.length === 0
    && texto.trim().length >= 2 && !semPermissao;

  if (cliente) {
    return (
      <div className="campo">
        <span className="rotulo">Cliente</span>
        <div className="linha">
          <div className="item" style={{ cursor: 'default' }}>
            <span className="cod">{cliente.razao_social}</span>
          </div>
          <button type="button" className="botao secundario estreito"
            style={{ flex: '0 0 auto' }}
            onClick={() => { aoEscolher(null); setTexto(''); }}>
            Trocar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="campo">
      <span className="rotulo">Cliente</span>
      <input
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Razão social ou CNPJ"
        autoComplete="off"
        aria-label="Buscar cliente da obra"
      />
      {semPermissao && <span className="dica">Seu perfil não consulta clientes.</span>}
      {achados && achados.length > 0 && (
        <div className="lista" style={{ marginTop: '.5rem' }}>
          {achados.map((c) => (
            <button key={c.id} type="button" className="item"
              onClick={() => aoEscolher(c)}>
              <span className="cod">{c.razao_social}</span>
              {c.cnpj_cpf && <span className="meta">{c.cnpj_cpf}</span>}
            </button>
          ))}
        </div>
      )}
      {vazio && (
        <div className="saida-do-beco">
          <span className="dica">Nenhum cliente com “{texto.trim()}”.</span>
          {pode('cadastro.editar') && (
            <button type="button" className="botao secundario"
              onClick={() => setCadastrando(true)}>
              Cadastrar “{texto.trim()}”
            </button>
          )}
        </div>
      )}
      <CadastroRapidoCliente
        aberto={cadastrando}
        nomeDigitado={texto.trim()}
        aoFechar={() => setCadastrando(false)}
        aoCriar={(c) => { setCadastrando(false); aoEscolher(c); }}
      />
    </div>
  );
}

// ── Parar a OS: suspender e retomar ──────────────────────────────────

/**
 * As duas paradas que o serviço não explica sozinho.
 *
 * Aqui havia um seletor com as treze situações. Desde a migração `0059` a
 * situação se DERIVA do andamento — e o seletor sumiu porque um estado que
 * se digita é um estado que mente: bastava alguém escolher "concluída"
 * para a OS se declarar pronta com ensaio na bancada.
 *
 * Sobrou o que nenhum fato consegue provar. Nenhum ensaio, nenhuma
 * amostra, nenhum relatório diz que a obra parou pela chuva.
 *
 * O motivo é obrigatório NOS DOIS SENTIDOS, e o de retomar não é
 * formalidade: "por que voltou?" é a pergunta da reunião de segunda tanto
 * quanto "por que parou?".
 */
export function FormParada({
  os, acao, aoFechar, aoPronto,
}: {
  os: Os; acao: 'suspender' | 'retomar'; aoFechar(): void; aoPronto(): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const suspendendo = acao === 'suspender';

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/os/${os.id}/${acao}`, {
        metodo: 'PATCH',
        corpo: { motivo: motivo.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo
      aberto
      titulo={suspendendo ? 'Suspender a OS' : 'Retomar a OS'}
      aoFechar={aoFechar}
    >
      <ErroDaApi erro={erro} />

      <p className="sub" style={{ marginBottom: '.6rem' }}>
        {suspendendo
          ? 'Enquanto suspensa, a OS congela: mexer num ensaio não a faz '
            + 'voltar a "ensaios em andamento". As três situações param onde '
            + 'estão até alguém retomar.'
          : 'Retomar não devolve a situação anterior — ela é recalculada do '
            + 'serviço. Se algum ensaio foi aprovado no meio do caminho, a OS '
            + 'volta adiante de onde parou.'}
      </p>

      {suspendendo && os.suspensa_motivo && (
        <p className="sub">Suspensão anterior: “{os.suspensa_motivo}”.</p>
      )}
      {!suspendendo && os.suspensa_motivo && (
        <p className="sub">Foi suspensa por: “{os.suspensa_motivo}”.</p>
      )}

      <Area
        rotulo="Motivo (obrigatório)"
        rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica={suspendendo
          ? 'Vai para o histórico e é o que responde “por que esta OS '
            + 'parou?” daqui a um mês, quando ninguém mais lembrar.'
          : 'O que destravou. Vai para o histórico junto com seu nome e a hora.'}
      />

      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={motivo.trim().length < 5 || enviando}
          onClick={() => { void enviar(); }}>
          {enviando ? 'Gravando…'
            : suspendendo ? 'Suspender' : 'Retomar'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Voltar
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Arquivar ou desarquivar uma OS (0288, RN-279).
 *
 * Arquivar não é situação: é tirar a OS do caminho — das listas, das filas
 * e do painel — sem apagar nada. A OS fica só leitura até alguém
 * desarquivar, e então volta exatamente como estava.
 */
export function FormArquivo({
  os, aoFechar, aoPronto,
}: { os: Os; aoFechar(): void; aoPronto(): void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const arquivando = !os.arquivada;

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      await api(arquivando ? '/os/arquivar' : '/os/desarquivar', {
        metodo: 'POST',
        corpo: { ids: [os.id], motivo: motivo.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo={arquivando ? 'Arquivar a OS' : 'Desarquivar a OS'}
      aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub" style={{ marginBottom: '.6rem' }}>
        {arquivando
          ? 'A OS sai da lista de OS, da fila do laboratório, do painel, do '
            + 'cronograma e do recebimento, e fica só leitura: não recebe '
            + 'amostra, ensaio nem lançamento. Nada é apagado — o Portal do '
            + 'cliente, a medição e os documentos emitidos continuam iguais.'
          : 'A OS volta às listas e às filas exatamente como estava: os ensaios '
            + 'pendentes voltam para a fila e a situação continua a que o '
            + 'serviço diz.'}
      </p>
      {!arquivando && os.arquivada_motivo && (
        <p className="sub">Foi arquivada por: “{os.arquivada_motivo}”.</p>
      )}
      <Area
        rotulo="Motivo (obrigatório)"
        rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica={arquivando
          ? 'Ex.: relatório entregue pelo Excel. Vai para o histórico da OS.'
          : 'O que mudou. Vai para o histórico junto com seu nome e a hora.'}
      />
      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={motivo.trim().length < 10 || enviando}
          onClick={() => { void enviar(); }}>
          {enviando ? 'Gravando…' : arquivando ? 'Arquivar' : 'Desarquivar'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

export function FormCancelar({
  os, aoFechar, aoPronto,
}: { os: Os; aoFechar(): void; aoPronto(): void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/os/${os.id}/cancelar`, {
        metodo: 'PATCH', corpo: { motivo: motivo.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo={`Cancelar a OS ${os.numero}`} aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />

      <Aviso tipo="atencao" titulo="Cancelar não é excluir.">
        A OS continua existindo, marcada como cancelada, com tudo o que já
        aconteceu nela. É o contrário de apagar: um serviço que existiu e não
        foi executado é informação, não sujeira.
      </Aviso>

      <Area
        rotulo="Motivo do cancelamento"
        rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica="Mínimo de 5 letras. Vai para o histórico e para a trilha de auditoria."
        autoFocus
      />

      <div className="rodape-dialogo">
        <button type="button" className="botao perigo"
          disabled={motivo.trim().length < 5 || enviando}
          onClick={() => { void enviar(); }}>
          {enviando ? 'Cancelando…' : 'Cancelar a OS'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Voltar
        </button>
      </div>
    </Dialogo>
  );
}

// ── Alterar os dados da OS ───────────────────────────────────────────

export function FormAlterar({
  os, aoFechar, aoPronto,
}: { os: Os; aoFechar(): void; aoPronto(): void }) {
  const { pode } = useSessao();
  /*
   * RN-194 (204): o responsável técnico é do admin. Quem não tem a
   * permissão vê o nome e não vê seletor — e não vale a pena nem pedir
   * a lista de funcionários para ele. Gestor e comercial saíram do
   * formulário de vez («deixe apenas responsável técnico»).
   */
  const podeDefinirRt = pode('os.definir_responsavel_tecnico');
  /* RN-227 (274): a lista é só de quem tem a assinatura de RT. */
  const { lista, semPermissao } = useResponsaveisTecnicos(podeDefinirRt);
  const [prioridade, setPrioridade] = useState(os.prioridade);
  const [proposta, setProposta] = useState(os.proposta_numero ?? '');
  const [prazo, setPrazo] = useState(dataIso(os.prazo_contratual));
  const [inicio, setInicio] = useState(dataIso(os.data_prevista_inicio));
  const [fim, setFim] = useState(dataIso(os.data_prevista_fim));
  const [realInicio, setRealInicio] = useState(dataIso(os.data_realizada_inicio));
  const [realFim, setRealFim] = useState(dataIso(os.data_realizada_fim));
  const [guarda, setGuarda] = useState(String(os.prazo_guarda_dias ?? ''));
  const [rt, setRt] = useState(os.responsavel_tecnico_id ?? '');
  const [observacoes, setObservacoes] = useState(os.observacoes ?? '');
  const [demonstracao, setDemonstracao] = useState(!!os.demonstracao);
  /* 278 · os dados da obra que se corrigem daqui (o número, não). */
  const [obraNome, setObraNome] = useState(os.obra_nome ?? '');
  const [municipio, setMunicipio] = useState(os.municipio ?? '');
  const [uf, setUf] = useState(os.uf ?? '');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const erroApi = erro instanceof ErroApi ? erro : null;
  const guardaNum = guarda.trim() ? Number(guarda) : undefined;
  const guardaOk = guardaNum === undefined
    || (Number.isInteger(guardaNum) && guardaNum >= 1 && guardaNum <= 3650);
  const nomeOk = obraNome.trim().length === 0 || obraNome.trim().length >= 3;
  const ufOk = uf.trim().length === 0 || /^[A-Za-z]{2}$/.test(uf.trim());
  /* Só vai o que mudou — mandar de volta o mesmo valor só enche a trilha. */
  const mudou = (novo: string, antigo: string | null | undefined) =>
    novo.trim() && novo.trim() !== (antigo ?? '').trim() ? novo.trim() : undefined;

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/os/${os.id}`, {
        metodo: 'PATCH',
        corpo: {
          prioridade,
          propostaNumero: proposta.trim() || undefined,
          prazoContratual: prazo || undefined,
          dataPrevistaInicio: inicio || undefined,
          dataPrevistaFim: fim || undefined,
          dataRealizadaInicio: realInicio || undefined,
          dataRealizadaFim: realFim || undefined,
          prazoGuardaDias: guardaNum,
          /* Só vai se mudou: mandar o mesmo RT de volta seria pedir ao
             servidor uma permissão que a pessoa pode não ter. */
          responsavelTecnicoId: podeDefinirRt && rt && rt !== (os.responsavel_tecnico_id ?? '')
            ? rt : undefined,
          observacoes: observacoes.trim() || undefined,
          demonstracao,
          obraNome: mudou(obraNome, os.obra_nome),
          obraMunicipio: mudou(municipio, os.municipio),
          obraUf: mudou(uf.toUpperCase(), os.uf),
          motivo: motivo.trim(),
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo={`Dados da OS ${os.numero}`} aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />

      {/*
        O NÚMERO da obra não aparece para editar: trocá-lo seria trocar o
        número da OS — o mesmo que já saiu impresso em etiqueta, boletim e
        relatório. Nome, município e UF se corrigem aqui (278); eles moram
        na obra, então valem para toda OS dessa obra.
      */}
      <Aviso tipo="info" titulo="O número não muda.">
        A OS se chama pelo número do projeto da obra ({os.obra_codigo}), e esse
        número já saiu impresso. Nome, município e UF da obra se corrigem
        aqui; número ou cliente errado se resolve cancelando esta OS e
        abrindo a certa — as duas continuam no histórico.
      </Aviso>

      <Campo
        rotulo="Nome da obra"
        value={obraNome}
        onChange={(e) => setObraNome(e.target.value)}
        erro={erroApi?.campo('obraNome')
          ?? (!nomeOk ? 'Ao menos três caracteres.' : undefined)}
        dica="Como aparece no contrato e no cabeçalho do relatório. Vale para toda OS desta obra."
      />

      <div className="linha">
        <Campo rotulo="Município" value={municipio}
          onChange={(e) => setMunicipio(e.target.value)}
          erro={erroApi?.campo('obraMunicipio')} />
        <Campo rotulo="UF" value={uf} maxLength={2}
          onChange={(e) => setUf(e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase())}
          erro={erroApi?.campo('obraUf')
            ?? (!ufOk ? 'Duas letras, como SC.' : undefined)} />
      </div>

      <label className="campo">
        <span className="rotulo">Prioridade</span>
        <select value={prioridade}
          onChange={(e) => setPrioridade(e.currentTarget.value)}>
          {['baixa', 'normal', 'alta', 'urgente'].map((p) => (
            <option key={p} value={p}>{rotulo(p)}</option>
          ))}
        </select>
      </label>

      <Campo
        rotulo="Proposta ou contrato"
        value={proposta}
        onChange={(e) => setProposta(e.target.value)}
        erro={erroApi?.campo('propostaNumero')}
        dica="O número da proposta comercial que originou o serviço."
      />

      <Campo
        rotulo="Prazo contratual"
        type="date"
        value={prazo}
        onChange={(e) => setPrazo(e.target.value)}
        erro={erroApi?.campo('prazoContratual')}
        dica="A data prometida ao cliente. É ela que faz a OS aparecer como atrasada."
      />

      <div className="linha">
        <Campo rotulo="Início previsto" type="date" value={inicio}
          onChange={(e) => setInicio(e.target.value)}
          erro={erroApi?.campo('dataPrevistaInicio')} />
        <Campo rotulo="Fim previsto" type="date" value={fim}
          onChange={(e) => setFim(e.target.value)}
          erro={erroApi?.campo('dataPrevistaFim')} />
      </div>

      <div className="linha">
        <Campo rotulo="Início realizado" type="date" value={realInicio}
          onChange={(e) => setRealInicio(e.target.value)}
          erro={erroApi?.campo('dataRealizadaInicio')} />
        <Campo rotulo="Fim realizado" type="date" value={realFim}
          onChange={(e) => setRealFim(e.target.value)}
          erro={erroApi?.campo('dataRealizadaFim')} />
      </div>

      <Campo
        rotulo="Prazo de guarda das amostras (dias)"
        inputMode="numeric"
        value={guarda}
        onChange={(e) => setGuarda(e.target.value.replace(/\D/g, ''))}
        erro={erroApi?.campo('prazoGuardaDias')
          ?? (!guardaOk ? 'De 1 a 3650 dias.' : undefined)}
        dica="Quanto tempo a amostra fica guardada depois do laudo aprovado. Padrão de 15 dias; o contrato pode dizer outro número."
      />

      {podeDefinirRt ? (
        <SeletorResponsavelTecnico
          valor={rt} aoEscolher={setRt}
          responsaveis={lista} semPermissao={semPermissao}
          atual={os.responsavel_tecnico_id
            ? { id: os.responsavel_tecnico_id, nome: os.responsavel_tecnico_nome ?? null }
            : null}
          erro={erroApi?.campo('responsavelTecnicoId')}
          dica="Quem assina o relatório. Só quem tem a assinatura de RT (RN-227); só o administrador do sistema define ou troca (RN-194)."
        />
      ) : (
        <p className="sub">
          <b>Responsável técnico:</b>{' '}
          {os.responsavel_tecnico_nome ?? 'não definido'} — só o
          administrador do sistema define ou troca.
        </p>
      )}

      <Area rotulo="Observações" rows={3} value={observacoes}
        onChange={(e) => setObservacoes(e.target.value)} />

      {/*
        * O rótulo de treino (0079).
        *
        * Aparece perto do fim e com a explicação inteira ao lado, porque
        * a consequência dele é invisível na tela: OS de demonstração NÃO
        * entra na medição. Um interruptor sem essa frase seria um jeito
        * silencioso de tirar serviço da fatura.
        *
        * O banco recusa trocá-lo depois que houve trabalho lançado — o
        * aviso aqui é para a pessoa saber ANTES de tentar.
        */}
      <label className="marcador">
        <input type="checkbox" checked={demonstracao}
          onChange={(e) => setDemonstracao(e.target.checked)} />
        <span>
          <b>OS de demonstração</b>
          <em className="dica">
            Existe para treinar e para conferir o processo. Não entra na
            medição, e em produção é a única em que uma conta de
            demonstração pode trabalhar. Depois que houver ensaio lançado,
            este rótulo não muda mais.
          </em>
        </span>
      </label>

      <Area
        rotulo="Motivo da alteração"
        rows={2}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        erro={erroApi?.campo('motivo')
          ?? (motivo.trim().length > 0 && motivo.trim().length < 5
            ? 'Escreva ao menos cinco caracteres.' : undefined)}
        dica="Obrigatório. É o que a trilha guarda ao lado do valor antigo e do novo — prazo e responsável mudam por uma razão, e é ela que se procura depois (UX-009)."
      />

      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={!guardaOk || !nomeOk || !ufOk || enviando || motivo.trim().length < 5}
          title={motivo.trim().length < 5 ? 'Informe o motivo da alteração.' : undefined}
          onClick={() => { void enviar(); }}>
          {enviando ? 'Gravando…' : 'Gravar'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>

      {/*
        A API usa COALESCE em todo campo: mandar vazio NÃO apaga o valor
        que existe. É proposital — mas o técnico não tem como saber, e
        descobriria apagando um campo e vendo que ele voltou.
      */}
      <p className="dica" style={{ marginTop: '.7rem' }}>
        Campo deixado em branco mantém o que já estava gravado. Para tirar um
        valor que existe, hoje é preciso pedir a quem administra o sistema —
        limpar campo por esta tela ainda não é possível.
      </p>
    </Dialogo>
  );
}

// ── Programação prévia das amostras ──────────────────────────────────

/**
 * Os métodos de ensaio, para marcar o que cada amostra prevista recebe.
 *
 * Vem de `/normas/metodos`, que é onde o catálogo mora. Se a rota não
 * existir ou o perfil não puder vê-la, a programação continua funcionando
 * sem ensaio marcado — a amostra prevista sozinha já serve para o
 * recebimento riscar da lista o que chegou.
 */
/**
 * Os métodos que a OS pode programar: os do escopo VIGENTE dela (0220,
 * `RN-218`). «O que libera para programação é apenas o que está no escopo
 * da OS» — Humberto, 02/10/2026. O catálogo inteiro deixa de ser
 * oferecido; o ensaio que não está no escopo entra pela revisão.
 */
function useMetodos(osId: string) {
  const [lista, setLista] = useState<Metodo[] | null>(null);
  useEffect(() => {
    const abortar = new AbortController();
    Promise.all([
      api<Metodo[]>('/normas/metodos', { sinal: abortar.signal }),
      api<Os>(`/os/${osId}`, { sinal: abortar.signal }),
    ])
      .then(([todos, os]) => {
        const doEscopo = new Set((os.escopo_por_metodo ?? []).map((m) => m.metodo_id));
        /* 312: a amostra prevista recebe ensaio de amostra; o método de
           campo (CPTU, 0254) mora na campanha CPTu. */
        setLista(todos.filter((m) => doEscopo.has(m.id) && m.categoria !== 'campo'));
      })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setLista([]); });
    return () => abortar.abort();
  }, [osId]);
  return lista;
}

/** O aviso de OS sem ensaio no escopo, com o caminho (0220, D1). */
function SemEnsaioNoEscopo({ osId }: { osId: string }) {
  return (
    <Aviso tipo="atencao" titulo="Esta OS ainda não tem ensaio no escopo">
      <p style={{ margin: '0 0 .5rem' }}>
        É o escopo que libera a programação (RN-218). As amostras podem ser
        previstas sem ensaio; para marcar ensaio nelas, lance antes o escopo
        contratado.
      </p>
      <Link className="botao secundario" to={`/os/${osId}?escopo=1`}>
        Lançar o escopo da OS
      </Link>
    </Aviso>
  );
}

/** Minúsculas e sem acento, para a busca não tropeçar em «Índice». */
const chato = (t: string) => t.normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase();

function EscolhaDeMetodos({
  metodos, marcados, aoMarcar,
}: {
  metodos: Metodo[] | null;
  marcados: string[];
  aoMarcar(ids: string[]): void;
}) {
  /*
    Cinquenta siglas numa grade é muito para o olho varrer — e quem sabe
    que quer «granulometria» não tem por que procurar GRAN entre elas. A
    caixa filtra por sigla, nome ou norma (Humberto, 14/09/2026); o que já
    está marcado nunca some da grade, senão parece que desmarcou.
  */
  const [busca, setBusca] = useState('');
  if (!metodos) return <span className="dica">Carregando os métodos…</span>;
  if (metodos.length === 0) {
    return (
      <span className="dica">
        Nenhum método disponível para marcar. A programação da amostra é
        gravada assim mesmo — o ensaio pode ser definido depois.
      </span>
    );
  }
  const termo = chato(busca.trim());
  const visiveis = termo
    ? metodos.filter((m) => marcados.includes(m.id)
      || chato(`${m.codigo} ${m.nome} ${m.norma ?? ''}`).includes(termo))
    : metodos;
  const marcadosNomes = metodos
    .filter((m) => marcados.includes(m.id)).map((m) => m.codigo);
  return (
    <div className="campo" role="group" aria-label="Ensaios previstos">
      <span className="rotulo">Ensaios previstos</span>
      <input
        type="search"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar pela sigla ou pelo nome do ensaio — ex.: GRAN, granulometria, limite"
        autoComplete="off"
        aria-label="Buscar ensaio"
        style={{ marginBottom: '.5rem' }}
      />
      {termo && visiveis.length === 0 && (
        <span className="dica">Nenhum ensaio com «{busca.trim()}».</span>
      )}
      <div className="escolha">
        {visiveis.map((m) => {
          const on = marcados.includes(m.id);
          return (
            <button
              key={m.id} type="button" aria-pressed={on}
              /*
                O botão mostra o CÓDIGO e o `title` leva o nome e a norma.
                Trinta nomes por extenso lado a lado viram uma parede, e é o
                código que está escrito na planilha da bancada — mas quem
                nunca viu "MEG-PASS" precisa de um caminho para descobrir o
                que é sem sair da tela.
              */
              title={[m.nome, m.norma].filter(Boolean).join(' — ')}
              onClick={() => aoMarcar(on
                ? marcados.filter((x) => x !== m.id)
                : [...marcados, m.id])}
            >
              {m.codigo}
            </button>
          );
        })}
      </div>
      <span className="dica">
        {marcadosNomes.length
          ? `Marcados: ${marcadosNomes.join(', ')}. `
          : ''}
        Vale para todas as amostras desta programação. O que ficar de fora
        pode ser acrescentado depois, amostra por amostra.
      </span>
    </div>
  );
}

/**
 * Prever N amostras de uma vez.
 *
 * Chamava-se «Programar N amostras» e o diálogo dizia «Programar
 * amostras» — a dois cliques do lugar onde se programa ENSAIO. Duas
 * coisas diferentes com a mesma palavra, e o Humberto reclamou da
 * confusão em 30/08/2026. Aqui se PREVÊ o que vai chegar; ensaio é outra
 * tela, e é a da OS.
 *
 * Doze amostras de um furo, uma a uma, são doze formulários dizendo a
 * mesma coisa doze vezes — e é onde nasce a divergência de digitação entre
 * a amostra 7 e a 8. Aqui se diz o furo, a profundidade inicial, o passo e
 * a quantidade; a identificação de cada uma sai daí, igual para todas.
 */
/**
 * Uma linha da relação colada (0188), já interpretada.
 *
 * O formato é o que sai de uma planilha ou de um e-mail: uma amostra por
 * linha, colunas separadas por TAB, «;» ou «|» — identificação, furo,
 * profundidade inicial, profundidade final, tipo. Só a identificação é
 * obrigatória; o resto entra quando vem. O tipo aceita «D», «I»,
 * «deformada», «indeformada» (e «bloco»/«shelby», que são indeformadas) e,
 * desde a 0209, «CP» / «CP moldado» para o corpo de prova moldado pelo cliente.
 */
export interface LinhaColada {
  n: number;
  identificacao: string;
  pontoInvestigacao?: string;
  profundidadeInicial?: number;
  profundidadeFinal?: number;
  tipoAmostra?: 'deformada' | 'indeformada' | 'cp_moldado';
  /* 0190: a coordenada da linha, de um jeito só — UTM ou graus. */
  utmE?: number;
  utmN?: number;
  utmFuso?: number;
  latitude?: number;
  longitude?: number;
  origemCoordenada?: 'projeto' | 'estimada';
  erro?: string;
}

/**
 * Um número como a planilha brasileira o escreve — «690.123,45»,
 * «6.987.654», «-27,1234» — ou como o GPS o exporta («-27.1234»).
 * Vírgula é sempre decimal. Ponto sem vírgula é decimal, a não ser que o
 * número seja «\d{1,3}(.\d{3})+» inteiro E grande demais para ser um
 * grau (≥ 1.000 depois de tirar os pontos): aí os pontos são de milhar.
 * «690.123» é E de UTM, não 690 graus; «-27.123» é latitude, não 27 mil.
 */
export function numeroDaPlanilha(bruto: string): number | undefined {
  const t = bruto.replace(/\s/g, '');
  if (!t) return undefined;
  if (t.includes(',')) {
    const x = Number(t.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(x) ? x : undefined;
  }
  if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) {
    const pontos = (t.match(/\./g) ?? []).length;
    const decimal = Number(t);
    /* «6.987.654» só pode ser milhar; «690.123» também (690 graus não
       existem); «-27.123» é grau. */
    if (pontos >= 2 || Math.abs(decimal) > 180) return Number(t.replace(/\./g, ''));
    return decimal;
  }
  const x = Number(t);
  return Number.isFinite(x) ? x : undefined;
}

/**
 * As colunas de coordenada de uma linha colada (0190), depois do tipo:
 * «E · N · fuso», «E · N» (com o fuso padrão do formulário), ou
 * «lat · long» em graus decimais — e, em qualquer posição entre elas,
 * «projeto» ou «estimada». Sem coluna nenhuma, sem coordenada.
 */
function coordenadaDaLinha(cols: string[], fusoPadrao?: number):
  { ok: true; valor: Partial<LinhaColada> } | { ok: false; erro: string } {
  let origem: 'projeto' | 'estimada' | undefined;
  const numeros: number[] = [];
  for (const c of cols) {
    const t = c.trim().toLowerCase();
    if (!t) continue;
    if (['projeto', 'proj'].includes(t)) { origem = 'projeto'; continue; }
    if (['estimada', 'est', 'estimado'].includes(t)) { origem = 'estimada'; continue; }
    const x = numeroDaPlanilha(t);
    if (x === undefined) return { ok: false, erro: `coordenada «${c.trim()}» não é número` };
    numeros.push(x);
  }
  if (numeros.length === 0) {
    return { ok: true, valor: origem ? { origemCoordenada: origem } : {} };
  }
  if (numeros.length === 2 && Math.abs(numeros[0]) <= 90 && Math.abs(numeros[1]) <= 180) {
    return { ok: true, valor: { latitude: numeros[0], longitude: numeros[1], origemCoordenada: origem } };
  }
  if (numeros.length === 2 || numeros.length === 3) {
    const [e, n, fuso] = numeros;
    const f = fuso ?? fusoPadrao;
    if (f === undefined) return { ok: false, erro: 'coordenada UTM sem o fuso (18 a 25) — diga na linha ou no fuso padrão' };
    if (!Number.isInteger(f) || f < 18 || f > 25) return { ok: false, erro: `fuso ${f} fora do Brasil (18 a 25)` };
    if (e < 100000 || e > 900000 || n < 1000000 || n > 10000000) {
      return { ok: false, erro: 'coordenada UTM fora da faixa (E 100.000–900.000, N 1.000.000–10.000.000)' };
    }
    return { ok: true, valor: { utmE: e, utmN: n, utmFuso: f, origemCoordenada: origem } };
  }
  return { ok: false, erro: 'coordenada: use «E · N · fuso» ou «lat · long»' };
}

export function interpretarRelacao(texto: string, fusoPadrao?: number): LinhaColada[] {
  const paraNum = (v: string | undefined) => {
    const t = (v ?? '').replace(',', '.').replace(/\s*m$/i, '').trim();
    if (!t) return { ok: true as const, valor: undefined };
    const x = Number(t);
    return Number.isFinite(x) && x >= 0
      ? { ok: true as const, valor: x } : { ok: false as const, valor: undefined };
  };
  const paraTipo = (v: string | undefined) => {
    const t = (v ?? '').trim().toLowerCase();
    if (!t) return { ok: true as const, valor: undefined };
    if (['d', 'def', 'deformada', 'saco'].includes(t)) return { ok: true as const, valor: 'deformada' as const };
    if (['i', 'ind', 'indeformada', 'bloco', 'shelby', 'tubo'].includes(t)) return { ok: true as const, valor: 'indeformada' as const };
    if (['cp', 'cp moldado', 'cp moldado pelo cliente', 'cp_moldado', 'corpo de prova'].includes(t)) return { ok: true as const, valor: 'cp_moldado' as const };
    return { ok: false as const, valor: undefined };
  };
  const vistas = new Map<string, number>();
  const linhas: LinhaColada[] = [];
  texto.split(/\r?\n/).forEach((bruta, i) => {
    if (!bruta.trim()) return;
    const cols = bruta.split(/\t|;|\|/).map((c) => c.trim());
    const [ident, furo, pi, pf, tipo, ...resto] = cols;
    const l: LinhaColada = { n: i + 1, identificacao: ident ?? '' };
    if (!l.identificacao) { l.erro = 'sem identificação'; linhas.push(l); return; }
    if (furo) l.pontoInvestigacao = furo;
    const a = paraNum(pi); const b = paraNum(pf); const t = paraTipo(tipo);
    const c = coordenadaDaLinha(resto, fusoPadrao);
    if (!a.ok || !b.ok) l.erro = 'profundidade não é número';
    else if (a.valor !== undefined && b.valor !== undefined && b.valor < a.valor) l.erro = 'profundidade final menor que a inicial';
    else if (!t.ok) l.erro = `tipo «${tipo}» não reconhecido (use D ou I)`;
    else if (!c.ok) l.erro = c.erro;
    l.profundidadeInicial = a.valor; l.profundidadeFinal = b.valor; l.tipoAmostra = t.valor;
    if (c.ok) Object.assign(l, c.valor);
    const k = l.identificacao.toLowerCase();
    if (vistas.has(k)) l.erro = l.erro ?? `repete a linha ${vistas.get(k)}`;
    else vistas.set(k, l.n);
    linhas.push(l);
  });
  return linhas;
}

/**
 * A coordenada prevista (0190), como campo de formulário: UTM (E, N,
 * fuso — SIRGAS 2000) ou graus decimais, e a origem — projeto ou
 * estimada. O mesmo bloco serve à série, à relação colada (como padrão
 * das linhas sem a sua) e à amostra avulsa.
 */
export interface CoordenadaPrevista {
  modo: 'utm' | 'graus';
  e: string; n: string; fuso: string;
  lat: string; lon: string;
  origem: 'projeto' | 'estimada';
}
export const COORDENADA_VAZIA: CoordenadaPrevista = {
  modo: 'utm', e: '', n: '', fuso: '22', lat: '', lon: '', origem: 'projeto',
};

/** O que vai para a API — ou nada, quando o bloco está em branco. */
export function corpoDaCoordenada(c: CoordenadaPrevista): Record<string, unknown> {
  const num = (v: string) => numeroDaPlanilha(v);
  if (c.modo === 'utm') {
    const e = num(c.e); const n = num(c.n); const fuso = num(c.fuso);
    if (e === undefined && n === undefined) return {};
    return { utmE: e, utmN: n, utmFuso: fuso, origemCoordenada: c.origem };
  }
  const lat = num(c.lat); const lon = num(c.lon);
  if (lat === undefined && lon === undefined) return {};
  return { latitude: lat, longitude: lon, origemCoordenada: c.origem };
}

/** A coordenada de uma linha colada, em uma célula — a própria, ou «(padrão)». */
function resumoDaCoordenada(l: LinhaColada, padrao: CoordenadaPrevista): string {
  const fmt = (x: number) => Math.round(x).toLocaleString('pt-BR');
  if (l.utmE !== undefined && l.utmN !== undefined) {
    return `${fmt(l.utmE)} E · ${fmt(l.utmN)} N (${l.utmFuso})${l.origemCoordenada === 'estimada' ? ' · est.' : ''}`;
  }
  if (l.latitude !== undefined && l.longitude !== undefined) {
    return `${l.latitude.toFixed(5).replace('.', ',')}, ${l.longitude.toFixed(5).replace('.', ',')}${l.origemCoordenada === 'estimada' ? ' · est.' : ''}`;
  }
  const p = corpoDaCoordenada(padrao);
  return Object.keys(p).length ? '(padrão)' : '—';
}

/** O que impede de enviar, antes de a API dizer o mesmo. */
export function erroDaCoordenada(c: CoordenadaPrevista): string | undefined {
  const num = (v: string) => numeroDaPlanilha(v);
  if (c.modo === 'utm') {
    const e = num(c.e); const n = num(c.n); const fuso = num(c.fuso);
    if (c.e.trim() && e === undefined) return 'E não é número';
    if (c.n.trim() && n === undefined) return 'N não é número';
    if ((e === undefined) !== (n === undefined)) return 'E e N andam juntos';
    if (e !== undefined && (fuso === undefined || fuso < 18 || fuso > 25)) return 'diga o fuso (18 a 25)';
    return undefined;
  }
  const lat = num(c.lat); const lon = num(c.lon);
  if (c.lat.trim() && lat === undefined) return 'latitude não é número';
  if (c.lon.trim() && lon === undefined) return 'longitude não é número';
  if ((lat === undefined) !== (lon === undefined)) return 'latitude e longitude andam juntas';
  if (lat !== undefined && (Math.abs(lat) > 90 || Math.abs(lon!) > 180)) return 'graus fora da faixa';
  return undefined;
}

export function CamposDeCoordenadaPrevista({
  valor, aoMudar, erroApi, dica,
}: {
  valor: CoordenadaPrevista; aoMudar(v: CoordenadaPrevista): void;
  erroApi?: ErroApi | null; dica?: string;
}) {
  const muda = (parte: Partial<CoordenadaPrevista>) => aoMudar({ ...valor, ...parte });
  const erroLocal = erroDaCoordenada(valor);
  return (
    <fieldset className="coordenada-prevista">
      <legend>Coordenada prevista <span className="sub">(opcional)</span></legend>
      <div className="seg" role="group" aria-label="Sistema da coordenada">
        <button type="button" aria-pressed={valor.modo === 'utm'}
          onClick={() => muda({ modo: 'utm' })}>UTM (SIRGAS 2000)</button>
        <button type="button" aria-pressed={valor.modo === 'graus'}
          onClick={() => muda({ modo: 'graus' })}>Graus decimais</button>
      </div>
      {valor.modo === 'utm' ? (
        <div className="linha">
          <Campo rotulo="E (m)" inputMode="decimal" value={valor.e}
            onChange={(e) => muda({ e: e.target.value })}
            erro={erroApi?.campo('utmE')} />
          <Campo rotulo="N (m)" inputMode="decimal" value={valor.n}
            onChange={(e) => muda({ n: e.target.value })}
            erro={erroApi?.campo('utmN')} />
          <Campo rotulo="Fuso" inputMode="numeric" value={valor.fuso}
            onChange={(e) => muda({ fuso: e.target.value.replace(/\D/g, '') })}
            erro={erroApi?.campo('utmFuso')} dica="18 a 25 (sul)" />
        </div>
      ) : (
        <div className="linha">
          <Campo rotulo="Latitude" inputMode="decimal" value={valor.lat}
            onChange={(e) => muda({ lat: e.target.value })}
            erro={erroApi?.campo('latitude')} dica="ex.: -26,9194" />
          <Campo rotulo="Longitude" inputMode="decimal" value={valor.lon}
            onChange={(e) => muda({ lon: e.target.value })}
            erro={erroApi?.campo('longitude')} dica="ex.: -49,0661" />
        </div>
      )}
      <label className="campo" htmlFor="origem-coordenada-prevista">
        <span className="rotulo">Origem da coordenada</span>
        <select id="origem-coordenada-prevista" value={valor.origem}
          onChange={(e) => muda({ origem: e.target.value as 'projeto' | 'estimada' })}>
          <option value="projeto">De projeto (locação)</option>
          <option value="estimada">Estimada</option>
        </select>
      </label>
      {(erroLocal || erroApi?.campo('origemCoordenada')) && (
        <span className="erro">{erroLocal ?? erroApi?.campo('origemCoordenada')}</span>
      )}
      <span className="dica">
        {dica ?? 'Onde se pretende coletar. A amostra recebida herda esta '
          + 'coordenada quando chega sem a sua; o mapa mostra a origem ao '
          + 'lado do alfinete.'}
      </span>
    </fieldset>
  );
}

/* ── 226 · o lote consome o escopo (`RN-207`) ─────────────────────── */

/** O que `POST /os/:id/programar` com `simular` responde. */
export interface PlanoDoLote {
  temEscopo: boolean;
  itens: {
    n: number; identificacao: string;
    /* 332 (`RN-262`): sem a vaga — o nome que a OS tem, ou amostra nova. */
    destino: 'prevista' | 'recebida' | 'nova';
    alvo?: string; metodosNovos: string[];
  }[];
  amostras: { teto: number | null; naOs: number; novas: number; excesso: number };
  metodos: { codigo: string; contratada: number | null; usados: number;
             pedidos: number; excesso: number; foraDoEscopo: boolean }[];
  excede: boolean;
  aviso: string | null;
}

const DESTINO: Record<PlanoDoLote['itens'][number]['destino'], string> = {
  prevista: 'usa a prevista de mesmo nome',
  recebida: 'usa a amostra recebida de mesmo nome',
  nova: 'nova amostra prevista',
};

/**
 * «Ao ultrapassar a quantidade prevista no escopo, deve avisar e solicitar
 *  revisão do escopo contratado (revisão de OS)» — Humberto, 29/09/2026.
 * O aviso e o caminho, juntos. Desde a 0220 (`RN-218`) o caminho é
 * «Incluir na OS»: a ficha abre pelo `?escopo=1` o diálogo certo — o
 * escopo livre, se nada foi programado ainda, ou a revisão, se já foi.
 */
export function PedirRevisaoDaOs({ osId, texto }: { osId: string; texto: string }) {
  const semEscopo = texto.startsWith('Esta OS ainda não tem ensaio no escopo');
  return (
    <Aviso tipo="erro"
      titulo={semEscopo ? 'Esta OS ainda não tem ensaio no escopo' : 'Passa do escopo contratado'}>
      <p style={{ margin: '0 0 .5rem' }}>{texto}</p>
      <Link className="botao secundario" to={`/os/${osId}?escopo=1`}>
        {semEscopo ? 'Lançar o escopo da OS' : 'Incluir na OS'}
      </Link>
    </Aviso>
  );
}

/** A recusa do escopo (409) que a tela mostra com o caminho para incluir. */
export const recusaDoEscopo = (msg: string) => msg.startsWith('Passa do escopo')
  || msg.startsWith('Esta OS ainda não tem ensaio no escopo');

function ConferenciaDoLote({ plano }: { plano: PlanoDoLote }) {
  const usa = plano.itens.filter((i) => i.destino !== 'nova').length;
  return (
    <div className="previa-relacao conferencia-lote">
      <p className="sub">
        <b>{usa}</b> amostra(s) que a OS já tem · <b>{plano.amostras.novas}</b> nova(s)
      </p>
      <div className="rolagem-lateral" style={{ maxHeight: '11rem', overflowY: 'auto' }}>
        <table className="dados compacta">
          <thead><tr><th>#</th><th>Na relação</th><th>Vai para</th><th>Amostra</th></tr></thead>
          <tbody>
            {plano.itens.map((i) => (
              <tr key={i.n} className={i.destino === 'nova' && plano.excede ? 'com-erro' : undefined}>
                <td className="mono">{i.n}</td>
                <td>{i.identificacao || '—'}</td>
                <td>{DESTINO[i.destino]}</td>
                <td className="mono">{i.alvo ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {plano.metodos.length > 0 && (
        <p className="sub">
          {plano.metodos.map((m) => (
            <span key={m.codigo} className={m.excesso > 0 ? 'aviso-linha' : undefined}>
              {m.codigo}{' '}
              {m.foraDoEscopo ? 'fora do escopo'
                : m.contratada === null ? `+${m.pedidos}`
                  : `${m.usados + m.pedidos}/${m.contratada}`}
              {' · '}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

export function FormProgramar({
  os, aoFechar, aoPronto,
}: { os: Os; aoFechar(): void; aoPronto(): void }) {
  const metodos = useMetodos(os.id);
  const [modo, setModo] = useState<'serie' | 'colar'>('serie');
  const [quantidade, setQuantidade] = useState('1');
  const [ponto, setPonto] = useState('');
  const [profundidade, setProfundidade] = useState('');
  const [passo, setPasso] = useState('');
  const [tipo, setTipo] = useState<'' | 'deformada' | 'indeformada' | 'cp_moldado'>('');
  const [texto, setTexto] = useState('');
  const [coord, setCoord] = useState<CoordenadaPrevista>(COORDENADA_VAZIA);
  const [marcados, setMarcados] = useState<string[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  /* 226: o lote primeiro se confere — o que usa da OS, o que nasce, se cabe */
  const [plano, setPlano] = useState<PlanoDoLote | null>(null);

  const n = Number(quantidade);
  const nOk = Number.isInteger(n) && n >= 1 && n <= 200;
  const erroApi = erro instanceof ErroApi ? erro : null;

  /* 0190: o fuso do bloco de coordenada vale para as linhas coladas que
     vieram só com E e N. */
  const fusoPadrao = coord.modo === 'utm' ? numeroDaPlanilha(coord.fuso) : undefined;
  const linhas = useMemo(
    () => (modo === 'colar' ? interpretarRelacao(texto, fusoPadrao) : []),
    [modo, texto, fusoPadrao]);
  const linhasComErro = linhas.filter((l) => l.erro);
  const colarOk = modo === 'colar' && linhas.length > 0 && linhas.length <= 500
    && linhasComErro.length === 0;
  const coordOk = erroDaCoordenada(coord) === undefined;
  const podeEnviar = (modo === 'serie' ? nOk : colarOk) && coordOk;
  const quantas = modo === 'serie' ? (nOk ? n : 0) : linhas.length;

  const paraNum = (v: string) => {
    const t = v.replace(',', '.').trim();
    if (!t) return undefined;
    const x = Number(t);
    return Number.isFinite(x) ? x : undefined;
  };

  /* Mudou o pedido, a conferência antiga não vale mais. */
  useEffect(() => { setPlano(null); },
    [modo, quantidade, ponto, profundidade, passo, tipo, texto, coord, marcados]);

  async function enviar(simular: boolean) {
    setErro(null);
    setEnviando(true);
    try {
      const r = await api<{ plano?: PlanoDoLote }>(`/os/${os.id}/programar`, {
        metodo: 'POST',
        corpo: { simular: simular || undefined, ...(modo === 'serie' ? {
          quantidade: n,
          pontoInvestigacao: ponto.trim() || undefined,
          profundidadeInicial: paraNum(profundidade),
          passo: paraNum(passo),
          metodos: marcados.length ? marcados : undefined,
          tipoAmostra: tipo || undefined,
          ...corpoDaCoordenada(coord),
        } : {
          linhas: linhas.map((l) => ({
            identificacao: l.identificacao,
            pontoInvestigacao: l.pontoInvestigacao,
            profundidadeInicial: l.profundidadeInicial,
            profundidadeFinal: l.profundidadeFinal,
            tipoAmostra: l.tipoAmostra,
            utmE: l.utmE, utmN: l.utmN, utmFuso: l.utmFuso,
            latitude: l.latitude, longitude: l.longitude,
            origemCoordenada: l.origemCoordenada,
          })),
          metodos: marcados.length ? marcados : undefined,
          tipoAmostra: tipo || undefined,
          ...corpoDaCoordenada(coord),
        }) },
      });
      if (simular) { setPlano(r.plano ?? null); return; }
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Prever amostras" aoFechar={aoFechar}>
      {erroApi?.status === 409 && recusaDoEscopo(erroApi.message)
        ? <PedirRevisaoDaOs osId={os.id} texto={erroApi.message} />
        : <ErroDaApi erro={erro} />}

      <p className="sub" style={{ marginBottom: '.6rem' }}>
        Várias amostras de uma vez, com os mesmos ensaios — antes de o
        material chegar. A programação é o que o recebimento usa para riscar
        da lista o que já chegou, e o que mostra, no meio do serviço, o que
        ainda falta receber. A exceção (uma amostra com um ensaio a mais) se
        ajusta depois, na grade.
      </p>

      <div className="seg" role="group" aria-label="Como informar as amostras"
        style={{ marginBottom: '.7rem' }}>
        <button type="button" aria-pressed={modo === 'serie'}
          onClick={() => setModo('serie')}>Em série (furo + passo)</button>
        <button type="button" aria-pressed={modo === 'colar'}
          onClick={() => setModo('colar')}>Colar a relação</button>
      </div>

      {modo === 'serie' ? (
        <>
          <Campo
            rotulo="Quantas amostras"
            inputMode="numeric"
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value.replace(/\D/g, ''))}
            erro={erroApi?.campo('quantidade')
              ?? (!nOk && quantidade ? 'De 1 a 200.' : undefined)}
          />
          <Campo
            rotulo="Ponto de investigação"
            value={ponto}
            onChange={(e) => setPonto(e.target.value)}
            erro={erroApi?.campo('pontoInvestigacao')}
            dica="Opcional. Preencha só quando as amostras forem todas do mesmo furo, vertical ou ponto de coleta (ex.: SP-01) — a identificação de cada uma sai daqui. Em branco, a origem se define no recebimento, amostra por amostra."
          />
          <div className="linha">
            <Campo
              rotulo="Profundidade inicial (m)"
              inputMode="decimal"
              value={profundidade}
              onChange={(e) => setProfundidade(e.target.value)}
              erro={erroApi?.campo('profundidadeInicial')}
            />
            <Campo
              rotulo="Passo (m)"
              inputMode="decimal"
              value={passo}
              onChange={(e) => setPasso(e.target.value)}
              erro={erroApi?.campo('passo')}
              dica="Opcional. Quanto desce a cada amostra, quando são do mesmo furo."
            />
          </div>
        </>
      ) : (
        <>
          <label className="campo" htmlFor="relacao-colada">
            <span className="rotulo">A relação, uma amostra por linha</span>
            <textarea id="relacao-colada" rows={8} value={texto} spellCheck={false}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={'AM-01\tSP-01\t1,00\t1,45\tD\t690.123\t6.987.654\t22\nAM-02\tSP-01\t2,00\t2,45\tD\t690.123\t6.987.654\t22\nBL-01\tPI-03\t\t\tI\t-26,9194\t-49,0661\testimada'} />
            <span className="dica">
              Cole direto da planilha. Colunas separadas por TAB, «;» ou «|»:
              identificação · furo/ponto · prof. inicial (m) · prof. final (m) ·
              tipo (D ou I) · coordenada — «E · N · fuso» em UTM (SIRGAS 2000),
              ou «lat · long» em graus decimais — e «projeto» ou «estimada».
              Só a identificação é obrigatória — é o nome que vai bater com a
              etiqueta no recebimento. Linha sem coordenada leva a do bloco
              abaixo, se houver.
            </span>
            {erroApi?.campo('linhas') && (
              <span className="erro">{erroApi.campo('linhas')}</span>
            )}
          </label>
          {linhas.length > 0 && (
            <div className="previa-relacao">
              <p className="sub">
                <b>{linhas.length}</b> amostra(s) na relação
                {linhasComErro.length > 0 && (
                  <> · <span className="aviso-linha">
                    {linhasComErro.length} linha(s) com problema — corrija antes de prever
                  </span></>
                )}
                {linhas.length > 500 && <> · <span className="aviso-linha">no máximo 500 por vez</span></>}
              </p>
              <div className="rolagem-lateral" style={{ maxHeight: '11rem', overflowY: 'auto' }}>
                <table className="dados compacta">
                  <thead><tr>
                    <th>#</th><th>Identificação</th><th>Furo/ponto</th>
                    <th>Prof. (m)</th><th>Tipo</th><th>Coordenada</th><th></th>
                  </tr></thead>
                  <tbody>
                    {linhas.map((l) => (
                      <tr key={l.n} className={l.erro ? 'com-erro' : undefined}>
                        <td className="mono">{l.n}</td>
                        <td>{l.identificacao || '—'}</td>
                        <td>{l.pontoInvestigacao ?? '—'}</td>
                        <td className="mono">
                          {l.profundidadeInicial !== undefined
                            ? `${String(l.profundidadeInicial).replace('.', ',')}${
                              l.profundidadeFinal !== undefined
                                ? ` – ${String(l.profundidadeFinal).replace('.', ',')}` : ''}`
                            : '—'}
                        </td>
                        <td>{l.tipoAmostra ?? (tipo || '—')}</td>
                        <td className="mono coordenada-colada">{resumoDaCoordenada(l, coord)}</td>
                        <td className="aviso-linha">{l.erro ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <label className="campo" htmlFor="tipo-previsto">
        <span className="rotulo">Tipo de amostra</span>
        <select id="tipo-previsto" value={tipo}
          onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="">não informar agora</option>
          <option value="deformada">Deformada (saco)</option>
          <option value="indeformada">Indeformada (bloco, Shelby, anel)</option>
          <option value="cp_moldado">CP moldado pelo cliente</option>
        </select>
        <span className="dica">
          É o GRUPO da amostra na programação: escolher «Indeformadas» na grade e
          apertar uma bateria marca só elas. Na relação colada, a coluna «tipo»
          de cada linha vale mais que este campo.
        </span>
      </label>

      <CamposDeCoordenadaPrevista valor={coord} aoMudar={setCoord} erroApi={erroApi}
        dica={modo === 'serie'
          ? 'A coordenada do furo — vale para todas as amostras da série. Onde se pretende coletar; a amostra recebida a herda quando chega sem a sua.'
          : 'O padrão para as linhas coladas sem coordenada própria — e o fuso das que vieram só com E e N.'} />

      {metodos !== null && metodos.length === 0 && <SemEnsaioNoEscopo osId={os.id} />}
      <EscolhaDeMetodos metodos={metodos} marcados={marcados}
        aoMarcar={setMarcados} />

      {modo === 'serie' && ponto.trim() && profundidade.trim() && (
        <Aviso tipo="info" titulo="A primeira vai se chamar assim:">
          {`${ponto.trim()} / ${profundidade.replace('.', ',')} m`}
        </Aviso>
      )}

      {plano && <ConferenciaDoLote plano={plano} />}
      {plano?.excede && plano.aviso && (
        <PedirRevisaoDaOs osId={os.id} texto={plano.aviso} />
      )}

      <div className="rodape-dialogo">
        {!plano ? (
          <button type="button" className="botao" disabled={!podeEnviar || enviando}
            onClick={() => { void enviar(true); }}>
            {enviando ? 'Conferindo…' : `Conferir ${quantas || ''} amostra(s)`
              + (marcados.length ? ` com ${marcados.length} ensaio(s)` : '')}
          </button>
        ) : (
          <button type="button" className="botao"
            disabled={!podeEnviar || enviando || plano.excede}
            onClick={() => { void enviar(false); }}>
            {enviando ? 'Gravando…' : 'Confirmar e prever'}
          </button>
        )}
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

/** Uma amostra prevista, quando a série não serve. */
export function FormAmostraPrevista({
  os, aoFechar, aoPronto,
}: { os: Os; aoFechar(): void; aoPronto(): void }) {
  const metodos = useMetodos(os.id);
  const [identificacao, setIdentificacao] = useState('');
  const [origem, setOrigem] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [coord, setCoord] = useState<CoordenadaPrevista>(COORDENADA_VAZIA);
  const [marcados, setMarcados] = useState<string[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const erroApi = erro instanceof ErroApi ? erro : null;
  const coordOk = erroDaCoordenada(coord) === undefined;

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/os/${os.id}/amostras-previstas`, {
        metodo: 'POST',
        corpo: {
          identificacaoPrevista: identificacao.trim() || undefined,
          origemPrevista: origem.trim() || undefined,
          observacoes: observacoes.trim() || undefined,
          metodos: marcados.length ? marcados : undefined,
          ...corpoDaCoordenada(coord),
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Acrescentar amostra prevista" aoFechar={aoFechar}>
      {erroApi?.status === 409 && recusaDoEscopo(erroApi.message)
        ? <PedirRevisaoDaOs osId={os.id} texto={erroApi.message} />
        : <ErroDaApi erro={erro} />}

      <Campo
        rotulo="Identificação prevista"
        value={identificacao}
        onChange={(e) => setIdentificacao(e.target.value)}
        erro={erroApi?.campo('identificacaoPrevista')}
        dica="Como o cliente vai chamar esta amostra. Ex.: SP-03 / 4,50 m."
        autoFocus
      />
      <Campo
        rotulo="Origem"
        value={origem}
        onChange={(e) => setOrigem(e.target.value)}
        erro={erroApi?.campo('origemPrevista')}
        dica="Furo, vertical, trecho ou local de coleta."
      />
      <Area rotulo="Observações" rows={2} value={observacoes}
        onChange={(e) => setObservacoes(e.target.value)} />

      <CamposDeCoordenadaPrevista valor={coord} aoMudar={setCoord} erroApi={erroApi} />

      {metodos !== null && metodos.length === 0 && <SemEnsaioNoEscopo osId={os.id} />}
      <EscolhaDeMetodos metodos={metodos} marcados={marcados}
        aoMarcar={setMarcados} />

      <div className="rodape-dialogo">
        <button type="button" className="botao" disabled={enviando || !coordOk}
          onClick={() => { void enviar(); }}>
          {enviando ? 'Gravando…' : 'Acrescentar'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Acrescentar item ao escopo contratado.
 *
 * ── Por que este diálogo só existiu na onda 106 ──────────────────────
 *
 * O escopo é a coluna «prevista» da medição (§13.3): sem ele o sistema
 * apura quanto foi executado e não tem contra o que comparar — nada de
 * saldo, nada de percentual, nada de excedente. A rota da API existe
 * desde a `0001`; o que faltava era a tela, e o texto do bloco vazio
 * dizia isso com todas as letras: «o catálogo de itens de medição ainda
 * não tem cadastro no sistema».
 *
 * Ele tem agora — `/itens-medicao`, com a lista de preços real da
 * Testesolo.
 *
 * ── O que o diálogo mostra além do nome do item ──────────────────────
 *
 * Se o sistema sabe APURAR aquele item sozinho. É a diferença entre um
 * escopo que vai se conferir sozinho contra os ensaios e um que vai
 * precisar de alguém digitando quantidade no fim do mês — e é melhor
 * saber disso na hora de contratar do que na hora de medir.
 *
 * ── O que ele NÃO faz ────────────────────────────────────────────────
 *
 * Não retira item. Retirar é revisão de escopo (`JanelaRevisarEscopo`, 283), com
 * motivo escrito, porque a medição pode já ter medido contra ele.
 * Reacrescentar o mesmo item com outra quantidade sobrescreve a linha
 * vigente — é o `ON CONFLICT` da rota, e é o caminho certo enquanto a OS
 * não foi revisada.
 */

export interface ItemCatalogo {
  id: string;
  codigo: string;
  descricao: string;
  categoria: string | null;
  unidade_nome: string;
  apura: boolean;
  /** 277: a família de cor (a do método ligado); null = pílula neutra. */
  escopo?: string | null;
}

export function FormEscopo(
  { os, aoFechar, aoPronto }:
  { os: Os; aoFechar: () => void; aoPronto: () => void },
) {
  /*
   * O escopo inteiro num gesto só (04/09/2026).
   *
   * «Ao abrir a OS normalmente a informação que temos é a quantidade de
   *  cada ensaio» (Testesolo, 02/09). A versão anterior era um item por
   * diálogo — procurar, escolher, digitar, gravar, abrir de novo —, e a
   * lista começava em mistura asfáltica. Aqui o catálogo aparece por
   * grupo, com uma caixa de quantidade em cada linha; o que tiver número
   * entra. O que já está no escopo aparece marcado e não se repete —
   * mudar quantidade é revisão, não é acréscimo.
   */
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<ItemCatalogo[] | null>(null);
  const [qtd, setQtd] = useState<Record<string, string>>({});
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState<string | null>(null);

  const erroApi = erro instanceof ErroApi ? erro : null;
  const jaNoEscopo = new Map((os.escopo ?? []).map((i) => [i.codigo, i]));

  useEffect(() => {
    const abortar = new AbortController();
    api<ItemCatalogo[]>('/itens-medicao', { sinal: abortar.signal })
      .then(setLista)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setLista([]); });
    return () => abortar.abort();
  }, []);

  const numeroDe = (v: string) => Number(v.replace(/\./g, '').replace(',', '.'));
  const escolhidos = (lista ?? []).filter((i) => {
    const v = (qtd[i.id] ?? '').trim();
    return v !== '' && Number.isFinite(numeroDe(v)) && numeroDe(v) > 0;
  });
  const invalidos = (lista ?? []).filter((i) => {
    const v = (qtd[i.id] ?? '').trim();
    return v !== '' && !(Number.isFinite(numeroDe(v)) && numeroDe(v) > 0);
  });

  /*
   * 277 · o resumo em pílulas (Humberto, 05/10/2026): «ao ir preenchendo
   * as quantidades de cada ensaio, coloque pílulas com o código de ensaio
   * (seguindo padrão de cor) e quantidade estipulada pra se ter o resumo na
   * própria janela. Caso tenha digitado errado, é possível excluir na
   * própria pílula (x)». A busca esconde as linhas de outros grupos — sem
   * o resumo, o «Lançar 6 itens» do rodapé contava itens que ninguém via.
   * A ordem é a da digitação; a pílula de quantidade inválida fica em
   * vermelho com o símbolo, e o × apaga a quantidade daquele item.
   */
  const porId = new Map((lista ?? []).map((i) => [i.id, i]));
  const pilulas = Object.entries(qtd)
    .filter(([id, v]) => v.trim() !== '' && porId.has(id))
    .map(([id, v]) => {
      const item = porId.get(id)!;
      const ok = Number.isFinite(numeroDe(v.trim())) && numeroDe(v.trim()) > 0;
      return { item, valor: v.trim(), ok };
    });
  const tirar = (id: string) => setQtd((a) => { const b = { ...a }; delete b[id]; return b; });
  const irPara = (codigo: string) => {
    setBusca(codigo);
    window.setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>(
        `input[aria-label="Quantidade de ${CSS.escape(codigo)}"]`);
      el?.focus(); el?.select();
    }, 0);
  };

  const q = busca.trim().toLowerCase();
  const visiveis = (lista ?? []).filter((i) => !q
    || i.codigo.toLowerCase().includes(q)
    || i.descricao.toLowerCase().includes(q)
    || (i.categoria ?? '').toLowerCase().includes(q));
  const grupos: { nome: string; itens: ItemCatalogo[] }[] = [];
  for (const i of visiveis) {
    const nome = (i.categoria ?? 'Outros').replace(/^Lab\./, '');
    const g = grupos.find((x) => x.nome === nome);
    if (g) g.itens.push(i); else grupos.push({ nome, itens: [i] });
  }

  async function enviar() {
    if (!escolhidos.length) return;
    setErro(null);
    try {
      for (const item of escolhidos) {
        setEnviando(item.codigo);
        await api(`/os/${os.id}/escopo`, {
          metodo: 'POST',
          corpo: {
            itemMedicaoId: item.id,
            quantidadePrevista: numeroDe(qtd[item.id]),
            observacoes: observacoes.trim() || undefined,
          },
        });
        setQtd((a) => { const b = { ...a }; delete b[item.id]; return b; });
      }
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(null); }
  }

  return (
    <Dialogo aberto titulo="Lançar o escopo contratado" rotulo={`OS ${os.numero}`}
      aoFechar={aoFechar}>
      <p className="sub">
        Digite a quantidade contratada ao lado de cada ensaio. O que tiver
        número entra no escopo; o resto fica de fora. Unidade: a do item.
      </p>
      <ErroDaApi erro={erro} />

      <Campo
        rotulo="Procurar no catálogo"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        dica="Código, descrição ou grupo. Ex.: CBR, adensamento, triaxial."
        autoFocus
      />

      <div className="resumo-escopo" role="group" aria-label="Resumo do que vai entrar no escopo">
        <span className="resumo-escopo-titulo">
          Vai entrar no escopo
          <span className="mono">{escolhidos.length}</span>
        </span>
        {pilulas.length === 0 ? (
          <span className="resumo-escopo-vazio">
            Nada ainda — cada quantidade digitada aparece aqui.
          </span>
        ) : (
          <ul className="pilulas-escopo">
            {pilulas.map(({ item, valor, ok }) => (
              <li key={item.id}
                className={`pilula-escopo escopo-${item.escopo ?? 'outros'}${ok ? '' : ' invalida'}`}>
                <button type="button" className="pilula-corpo"
                  title={ok
                    ? `${item.codigo} · ${valor} ${item.unidade_nome} — ${item.descricao}`
                    : `Quantidade inválida em ${item.codigo}: «${valor}». Toque para corrigir.`}
                  onClick={() => irPara(item.codigo)}>
                  {!ok && <span aria-hidden="true">⚠</span>}
                  <span className="mono cod">{item.codigo}</span>
                  <span className="mono q">{ok ? numero(numeroDe(valor), 3, true) : valor}</span>
                </button>
                <button type="button" className="pilula-tirar"
                  aria-label={`Tirar ${item.codigo} do resumo`}
                  title={`Tirar ${item.codigo}`}
                  disabled={!!enviando}
                  onClick={() => tirar(item.id)}>
                  <svg viewBox="0 0 32 32" width="14" height="14" aria-hidden="true" focusable="false">
                    <path fill="currentColor"
                      d="M24 9.4 22.6 8 16 14.6 9.4 8 8 9.4l6.6 6.6L8 22.6 9.4 24l6.6-6.6 6.6 6.6 1.4-1.4-6.6-6.6z" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {lista === null ? (
        <span className="dica">Carregando o catálogo…</span>
      ) : visiveis.length === 0 ? (
        <span className="dica">
          Nenhum item encontrado. O catálogo é a lista de preços da
          Testesolo — se o serviço não está nele, ele precisa ser
          cadastrado antes de ser contratado.
        </span>
      ) : (
        <div className="escopo-grade rola" role="group" aria-label="Itens do catálogo">
          {grupos.map((g) => (
            <div key={g.nome} className="escopo-grupo">
              <h4>{g.nome}</h4>
              {g.itens.map((i) => {
                const ja = jaNoEscopo.get(i.codigo);
                const tem = !ja && (qtd[i.id] ?? '').trim() !== '';
                return (
                  <label key={i.id}
                    className={`escopo-linha escopo-${i.escopo ?? 'outros'}${ja ? ' ja' : ''}${tem ? ' tem' : ''}`}>
                    <span className="mono cod">{i.codigo}</span>
                    <span className="desc">
                      {i.descricao}
                      <span className="meta">
                        {i.unidade_nome}{!i.apura && ' · apuração manual'}
                      </span>
                    </span>
                    {ja ? (
                      <span className="qtd ja" title="Já está no escopo. Para mudar a quantidade, abra uma revisão.">
                        {numero(ja.quantidade_prevista, 0, true)} no escopo
                      </span>
                    ) : (
                      <input
                        className="qtd" inputMode="decimal" placeholder="—"
                        aria-label={`Quantidade de ${i.codigo}`}
                        aria-invalid={invalidos.includes(i) || undefined}
                        value={qtd[i.id] ?? ''}
                        onChange={(e) => setQtd((a) => ({ ...a, [i.id]: e.target.value }))}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {invalidos.length > 0 && (
        <Aviso tipo="erro">
          Quantidade inválida em {invalidos.map((i) => i.codigo).join(', ')}.
        </Aviso>
      )}
      {erroApi?.campo('quantidadePrevista') && (
        <Aviso tipo="erro">{erroApi.campo('quantidadePrevista')}</Aviso>
      )}
      {escolhidos.some((i) => !i.apura) && (
        <Aviso tipo="atencao" titulo="Há item que não se apura sozinho">
          {escolhidos.filter((i) => !i.apura).map((i) => i.codigo).join(', ')}:
          o sistema não sabe de qual ensaio sai a quantidade. Entra no
          escopo e conta como previsto, mas o executado vai precisar ser
          lançado à mão.
        </Aviso>
      )}

      <Area rotulo="Observações (valem para os itens lançados agora)" rows={2}
        value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />

      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={!escolhidos.length || invalidos.length > 0 || !!enviando}
          onClick={() => { void enviar(); }}>
          {enviando ? `Gravando ${enviando}…`
            : escolhidos.length
              ? `Lançar ${escolhidos.length} ${escolhidos.length === 1 ? 'item' : 'itens'}`
              : 'Digite ao menos uma quantidade'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={!!enviando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

/*
 * A revisão do escopo mora em `RevisarEscopo.tsx` desde a 283 (`RN-229`):
 * uma janela só, com a quantidade nova de cada ensaio e o catálogo para
 * incluir — no lugar do diálogo que o «Incluir na OS» e o «Revisar escopo»
 * abriam (o mesmo, com dois títulos).
 */

/* ── 227 · a amostra que nunca vai chegar sai (`RN-208`) ─────────────── */

/**
 * «Eu preciso deletar as 2 amostras "por chegar" pois elas foram criadas
 *  na parte de programar ensaios, e não são amostras a chegar» — Humberto,
 *  30/09/2026. Só a previsão que ainda não chegou; o motivo vai para a
 *  trilha. Os ensaios marcados nela saem junto.
 */
export function FormExcluirPrevista({
  osId, prevista, aoFechar, aoPronto,
}: {
  osId: string;
  prevista: { id: string; nome: string | null; ensaios?: number };
  aoFechar(): void;
  aoPronto(): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const erroApi = erro instanceof ErroApi ? erro : null;

  async function excluir() {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/os/${osId}/amostras-previstas/${prevista.id}`, {
        metodo: 'DELETE', corpo: { motivo: motivo.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Excluir amostra por chegar" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub" style={{ marginBottom: '.6rem' }}>
        «{prevista.nome || 'sem identificação'}» sai da OS: ela ainda não chegou,
        então não há medida nem ensaio a perder
        {prevista.ensaios ? ` — os ${prevista.ensaios} ensaio(s) marcados nela saem junto` : ''}.
        A exclusão fica registrada na trilha com o motivo.
      </p>
      <Campo
        rotulo="Por que esta amostra não vai chegar"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        erro={erroApi?.campo('motivo')}
        dica="Ex.: criada por engano na programação em lote; o cliente cancelou a coleta."
        autoFocus
      />
      <div className="rodape-dialogo">
        <button type="button" className="botao perigo"
          disabled={enviando || motivo.trim().length < 10}
          onClick={() => { void excluir(); }}>
          {enviando ? 'Excluindo…' : 'Excluir amostra por chegar'}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * A previsão de chegada das amostras por chegar — onda 240, 0226 (D5).
 *
 * «Previsão do cliente ou da própria Testesolo (quando a coleta é da
 *  Testesolo), deve ser inserida na OS (em caso de amostra de cliente)»
 *  — Humberto, 02/10/2026.
 *
 * Uma data para VÁRIAS amostras de uma vez: a carga chega junta. Todas as
 * que ainda não chegaram vêm marcadas; desmarque as que vêm em outro dia.
 * Mudar uma previsão que já existia pede o motivo — a primeira fica
 * guardada, e é contra ela que o painel mede a chegada atrasada (RN-216).
 */
export function FormPrevisaoChegada({
  osId, previstas, aoFechar, aoPronto,
}: {
  osId: string;
  previstas: AmostraPrevista[];
  aoFechar(): void;
  aoPronto(): void;
}) {
  const pendentes = previstas.filter((p) => !p.atendida);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set(pendentes.map((p) => p.id)));
  const [dia, setDia] = useState('');
  const [origem, setOrigem] = useState<'' | 'cliente' | 'testesolo'>('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const erroApi = erro instanceof ErroApi ? erro : null;

  const mudaExistente = pendentes.some((p) => marcadas.has(p.id)
    && p.previsao_chegada && dataIso(p.previsao_chegada) !== dia);

  function alternar(id: string) {
    const n = new Set(marcadas);
    if (n.has(id)) n.delete(id); else n.add(id);
    setMarcadas(n);
  }

  async function gravar(semData = false) {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/os/${osId}/amostras-previstas/previsao`, {
        metodo: 'PATCH',
        corpo: {
          ids: [...marcadas],
          previsaoChegada: semData ? null : dia,
          ...(origem ? { origem } : {}),
          ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Previsão de chegada" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub" style={{ marginBottom: '.6rem' }}>
        Quando estas amostras devem chegar ao laboratório. É o que o painel usa
        para mostrar as chegadas dos próximos dias e a amostra que já devia ter
        chegado.
      </p>
      <ul className="lista-previsao">
        {pendentes.map((p) => (
          <li key={p.id}>
            <label>
              <input type="checkbox" checked={marcadas.has(p.id)}
                onChange={() => alternar(p.id)} />
              <span>{p.identificacao_prevista || 'sem identificação'}</span>
              <span className="sub">
                {p.previsao_chegada
                  ? ` · previsto ${data(p.previsao_chegada)}`
                  : ' · sem previsão'}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <Campo rotulo="Chega em" type="date" value={dia}
        onChange={(e) => setDia(e.target.value)}
        erro={erroApi?.campo('previsaoChegada')} autoFocus />
      <label className="campo">
        <span className="rotulo">De quem é a data</span>
        <select value={origem} onChange={(e) => setOrigem(e.target.value as typeof origem)}>
          <option value="">Pela origem da amostra</option>
          <option value="cliente">Previsão do cliente</option>
          <option value="testesolo">Coleta da Testesolo</option>
        </select>
      </label>
      {mudaExistente && (
        <Campo rotulo="Por que a previsão mudou" value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          erro={erroApi?.campo('motivo')}
          dica="Ex.: o cliente adiou a coleta; a sonda quebrou. A primeira previsão continua guardada." />
      )}
      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={enviando || marcadas.size === 0 || !dia
            || (mudaExistente && motivo.trim().length < 5)}
          onClick={() => { void gravar(); }}>
          {enviando ? 'Gravando…' : `Prever ${marcadas.size} amostra(s)`}
        </button>
        <button type="button" className="botao secundario"
          onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}
